import type { Placement } from '@popperjs/core/lib/enums'
import cx from 'classnames'
import isFunction from 'lodash/isFunction'
import type React from 'react'
import { type ReactNode, useEffect, useState } from 'react'
import { type StrictModifier, usePopper } from 'react-popper'

import { useEscapeLayer } from '../../escape'
import useClickOutside from '../../hooks/useClickOutside'
import css from './DropdownContainer.module.scss'

type Props = {
  children: ReactNode | ((close: () => void) => ReactNode)
  renderButton: (
    onSetRef: (
      value:
        | ((prevState: HTMLElement | null) => HTMLElement | null)
        | HTMLElement
        | null
    ) => void,
    onClick: () => void,
    isOpen: boolean
  ) => ReactNode
  position?: Placement
  customClassName?: string
  offset?: {
    x?: number
    y?: number
  }
  withOverflow?: boolean
  noCloseByOutsideClick?: boolean
  wrapperClassName?: string
  onOutsideClick?: () => void
}

export const DropdownContainer: React.FC<Props> = ({
  children,
  renderButton,
  position,
  customClassName,
  offset,
  withOverflow,
  noCloseByOutsideClick,
  wrapperClassName,
  onOutsideClick,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [modifiers, setModifiers] = useState<StrictModifier[]>([])
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null)
  const [targetRef, setTargetRef] = useState<HTMLElement | null>(null)

  const clickOutside = useClickOutside<HTMLDivElement>(true, () => {
    if (isOpen && !noCloseByOutsideClick) {
      setIsOpen(false)
      onOutsideClick?.()
    }
  })

  useEscapeLayer(isOpen, () => {
    setIsOpen(false)
    onOutsideClick?.()
  })

  useEffect(() => {
    const x = offset?.x || 0
    const y = offset?.y || 8

    setModifiers((prevState) => {
      return [
        ...prevState,
        {
          name: 'offset',
          options: {
            offset: [x, y],
          },
        },
      ]
    })
  }, [offset])

  useEffect(() => {
    if (!withOverflow) {
      return
    }

    if (isOpen) {
      document.body.style.overflow = 'hidden'
    }

    const removeOverflow = setTimeout(() => {
      document.body.style.overflow = 'auto'
    }, 1000)

    return () => clearInterval(removeOverflow)
  }, [isOpen, withOverflow])

  const { styles, attributes } = usePopper(targetRef, containerRef, {
    modifiers,
    placement: position,
  })

  const handleToggle = () => setIsOpen((prev) => !prev)
  const close = () => setIsOpen(false)

  return (
    <div
      {...clickOutside}
      className={wrapperClassName}
      onClick={(e) => e.stopPropagation()}
    >
      {renderButton(setTargetRef, handleToggle, isOpen)}

      {isOpen && (
        <div
          ref={setContainerRef}
          style={styles.popper}
          {...attributes.popper}
          className={cx(css.container, customClassName)}
        >
          <>{isFunction(children) ? children(close) : children}</>
        </div>
      )}
    </div>
  )
}
